import type {
  IChartApiBase, IPrimitivePaneRenderer, IPrimitivePaneView, ISeriesApi, ISeriesPrimitive, Logical,
  SeriesAttachedParameter, SeriesType, Time,
} from "lightweight-charts";
import { withAlpha } from "./drawing/geometry";
import type { Rect } from "./SignalOverlay";
import type { SrLevel, Touch } from "./structure";
import type { Channel, StructureRead, Swing } from "./marketStructure";

type Target = Parameters<IPrimitivePaneRenderer["draw"]>[0];
type Ctx = CanvasRenderingContext2D;

export interface StructureColors {
  /** Support / resistance lines and tags: one neutral ink, the side says which. */
  ink: string;
  surface: string;
  up: string;
  down: string;
  muted: string;
  font: string;
}

export interface StructureData {
  levels: SrLevel[];
  /** The bias's last 3 swing highs and lows; null hides them. */
  swings: { highs: Touch[]; lows: Touch[] } | null;
  /** Market structure on any timeframe: every swing, the trend they make and its channel. */
  market?: { swings: Swing[]; read: StructureRead; channel: Channel | null } | null;
}

const TAG_H = 16;
const TAG_PAD = 5;

/**
 * The chart's structure, drawn for checking by eye. On 4h: support /
 * resistance lines from the first turn to the right edge with a dot on every
 * candle that turned there, and the 4h bias's last swing highs and lows
 * tagged HH / LH / HL / LL. On any timeframe ("Structure"): every swing
 * joined and tagged, the trend channel, and the swing that would break the
 * trend. Lines under the candles, tags in the top layer.
 */
export class StructurePrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApiBase<Time> | null = null;
  private series: ISeriesApi<SeriesType, Time> | null = null;
  private requestUpdate: (() => void) | null = null;
  private data: StructureData = { levels: [], swings: null };
  private times: number[] = [];
  private show = { sr: true, swings: true, market: false };
  private insets: { rightLane: number; legend: Rect | null } = { rightLane: 0, legend: null };
  private readonly views: readonly IPrimitivePaneView[];

  constructor(private readonly colors: StructureColors, private readonly format: (price: number) => string) {
    const lines: IPrimitivePaneRenderer = { draw: () => {}, drawBackground: t => this.drawLines(t) };
    const tags: IPrimitivePaneRenderer = { draw: t => this.drawTags(t) };
    this.views = [
      { zOrder: () => "bottom", renderer: () => lines },
      { zOrder: () => "top", renderer: () => tags },
    ];
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>) {
    this.chart = chart;
    this.series = series;
    this.requestUpdate = requestUpdate;
  }

  detached() {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews() {
    return this.views;
  }

  /** Levels and swings, against the chart's candle times (ascending, seconds). */
  setData(data: StructureData, times: number[]) {
    this.data = data;
    this.times = times;
    this.requestUpdate?.();
  }

  setVisible(show: { sr: boolean; swings: boolean; market: boolean }) {
    this.show = show;
    this.requestUpdate?.();
  }

  setInsets(insets: { rightLane: number; legend: Rect | null }) {
    this.insets = insets;
    this.requestUpdate?.();
  }

  /** x of a candle's centre by its open time (touches are always candles on this chart). */
  private xOf(): ((time: number) => number | null) | null {
    const ts = this.chart?.timeScale();
    if (!ts) return null;
    const x0 = ts.logicalToCoordinate(0 as Logical);
    const x1 = ts.logicalToCoordinate(1 as Logical);
    if (x0 === null || x1 === null) return null;
    const times = this.times;
    return (time: number) => {
      let lo = 0, hi = times.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (times[mid] === time) return x0 + mid * (x1 - x0);
        if (times[mid]! < time) lo = mid + 1; else hi = mid - 1;
      }
      return null;
    };
  }

  private drawLines(target: Target) {
    const s = this.series;
    const xOf = this.xOf();
    if (!s || !xOf) return;
    const c = this.colors;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      if (this.show.sr) {
        for (const l of this.data.levels) {
          const y = s.priceToCoordinate(l.price);
          const x = xOf(l.touches[0]!.time);
          if (y === null || x === null) continue;
          // More touches, firmer line: 3+ solid and thicker, 2 solid, 1 dashed.
          const n = l.touches.length;
          ctx.strokeStyle = withAlpha(c.ink, n >= 3 ? 0.75 : n === 2 ? 0.6 : 0.45);
          ctx.lineWidth = n >= 3 ? 1.5 : 1;
          ctx.setLineDash(n === 1 ? [5, 4] : []);
          ctx.beginPath();
          ctx.moveTo(Math.max(0, x), Math.round(y) + 0.5);
          ctx.lineTo(mediaSize.width, Math.round(y) + 0.5);
          ctx.stroke();
          ctx.setLineDash([]);
          // A dot where each candle turned (its own high or low, not the level's mean).
          ctx.fillStyle = c.ink;
          for (const t of l.touches) {
            const tx = xOf(t.time), ty = s.priceToCoordinate(t.price);
            if (tx === null || ty === null) continue;
            ctx.beginPath();
            ctx.arc(tx, ty, 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      // The swings the bias reads, joined: highs above, lows below.
      const sw = this.show.swings ? this.data.swings : null;
      if (sw) {
        ctx.strokeStyle = withAlpha(c.muted, 0.7);
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        for (const pts of [sw.highs, sw.lows]) {
          ctx.beginPath();
          let started = false;
          for (const p of pts) {
            const x = xOf(p.time), y = s.priceToCoordinate(p.price);
            if (x === null || y === null) continue;
            if (started) ctx.lineTo(x, y); else ctx.moveTo(x, y);
            started = true;
          }
          ctx.stroke();
        }
        ctx.setLineDash([]);
      }

      const m = this.show.market ? this.data.market : null;
      if (m) this.drawMarketLines(ctx, m, xOf, mediaSize.width);
    });
  }

  /** The zigzag through the swings, the trend channel, and the line the trend must hold. */
  private drawMarketLines(
    ctx: Ctx, m: NonNullable<StructureData["market"]>, xOf: (time: number) => number | null, width: number,
  ) {
    const s = this.series!;
    const c = this.colors;
    const tone = m.read.trend === "up" ? c.up : m.read.trend === "down" ? c.down : c.muted;

    // The channel: a line through the trend side's last two swings, extended
    // right, and its parallel through the furthest swing on the other side.
    const ch = m.channel;
    const xa = ch ? xOf(ch.t1) : null, xb = ch ? xOf(ch.t2) : null;
    const ya = ch ? s.priceToCoordinate(ch.p1) : null, yb = ch ? s.priceToCoordinate(ch.p2) : null;
    const yo = ch ? s.priceToCoordinate(ch.p1 + ch.offset) : null;
    if (ch && xa !== null && xb !== null && ya !== null && yb !== null && yo !== null && xb !== xa) {
      const slope = (yb - ya) / (xb - xa);
      const yAt = (x: number) => ya + slope * (x - xa);
      const dy = yo - ya;
      ctx.fillStyle = withAlpha(tone, 0.05);
      ctx.beginPath();
      ctx.moveTo(xa, yAt(xa));
      ctx.lineTo(width, yAt(width));
      ctx.lineTo(width, yAt(width) + dy);
      ctx.lineTo(xa, yAt(xa) + dy);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = withAlpha(tone, 0.75);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([7, 4]);
      for (const off of [0, dy]) {
        ctx.beginPath();
        ctx.moveTo(xa, yAt(xa) + off);
        ctx.lineTo(width, yAt(width) + off);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // Swing to swing, the way price travelled; fainter to the leg still running.
    const pts: { x: number; y: number; confirmed: boolean }[] = [];
    for (const p of m.swings) {
      const x = xOf(p.time), y = s.priceToCoordinate(p.price);
      if (x !== null && y !== null) pts.push({ x, y, confirmed: p.confirmed });
    }
    ctx.strokeStyle = withAlpha(c.muted, 0.75);
    ctx.lineWidth = 1;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!, b = pts[i]!;
      ctx.setLineDash(b.confirmed ? [3, 3] : [1, 3]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // The swing the trend must hold, out to the right edge.
    const pv = m.read.pivot;
    const xp = pv ? xOf(pv.time) : null, yp = pv ? s.priceToCoordinate(pv.price) : null;
    if (pv && xp !== null && yp !== null) {
      ctx.strokeStyle = withAlpha(m.read.broken ? c.ink : tone, 0.8);
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.moveTo(xp, Math.round(yp) + 0.5);
      ctx.lineTo(width, Math.round(yp) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  private drawTags(target: Target) {
    const s = this.series;
    const xOf = this.xOf();
    if (!s || !xOf) return;
    const c = this.colors;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      ctx.save();
      const placed: Rect[] = this.insets.legend ? [this.insets.legend] : [];
      const clash = (r: Rect) => placed.some(b => r.x < b.x + b.w && r.x + r.w > b.x && r.y < b.y + b.h && r.y + r.h > b.y);

      // Swing tags first: they sit on the candles they name, so the level tags move for them.
      const m = this.show.market ? this.data.market : null;
      if (m) {
        ctx.font = `700 10px ${c.font}`;
        ctx.textAlign = "center";
        ctx.lineJoin = "round";
        for (const p of m.swings) {
          const x = xOf(p.time), y = s.priceToCoordinate(p.price);
          if (x === null || y === null || x < 0 || x > mediaSize.width) continue;
          const high = p.kind === "high";
          const up = p.label === "HH" || p.label === "HL";
          const down = p.label === "LH" || p.label === "LL";
          // The running leg's extreme isn't a swing yet: "?" until price turns far enough.
          const word = p.confirmed ? p.label : `${p.label}?`;
          const tone = !p.confirmed ? c.muted : up ? c.up : down ? c.down : c.muted;
          const ty = high ? y - 6 : y + 6;
          ctx.textBaseline = high ? "bottom" : "top";
          ctx.strokeStyle = c.surface;
          ctx.lineWidth = 3;
          ctx.strokeText(word, x, ty);
          ctx.fillStyle = tone;
          ctx.fillText(word, x, ty);
          const w = ctx.measureText(word).width + 4;
          placed.push({ x: x - w / 2, y: high ? ty - 12 : ty, w, h: 12 });
        }
        // The swing the trend must hold, named at the right edge.
        const pv = m.read.pivot;
        const yp = pv ? s.priceToCoordinate(pv.price) : null;
        if (pv && yp !== null) {
          const upTrend = m.read.trend === "up";
          const text = m.read.broken
            ? `Closed ${upTrend ? "below" : "above"} ${this.format(pv.price)}: ${upTrend ? "uptrend" : "downtrend"} broken`
            : `${upTrend ? "Uptrend holds above" : "Downtrend holds below"} ${this.format(pv.price)}`;
          ctx.font = `600 10px ${c.font}`;
          const w = ctx.measureText(text).width + 2 * TAG_PAD;
          const x = Math.max(2, mediaSize.width - this.insets.rightLane - w - 4);
          // On the side of the line away from the trend: under a higher low, over a lower high.
          const r = { x, y: upTrend ? yp + 3 : yp - TAG_H - 3, w, h: TAG_H };
          if (!clash(r) && r.y >= 0 && r.y + TAG_H <= mediaSize.height) {
            placed.push(r);
            this.tag(ctx, r, text, m.read.broken ? c.ink : upTrend ? c.up : c.down);
          }
        }
      }

      const sw = this.show.swings ? this.data.swings : null;
      if (sw) {
        ctx.font = `700 10px ${c.font}`;
        ctx.textAlign = "center";
        ctx.lineJoin = "round";
        const mark = (pts: Touch[], high: boolean) => {
          pts.forEach((p, i) => {
            const x = xOf(p.time), y = s.priceToCoordinate(p.price);
            if (x === null || y === null) return;
            const prev = pts[i - 1];
            // The first of the three is where the count starts; each next one is
            // higher or lower than the one before.
            const word = !prev ? (high ? "H" : "L")
              : p.price > prev.price ? (high ? "HH" : "HL")
              : p.price < prev.price ? (high ? "LH" : "LL")
              : (high ? "EH" : "EL");
            const tone = !prev || p.price === prev.price ? c.muted : p.price > prev.price ? c.up : c.down;
            const ty = high ? y - 6 : y + 6;
            ctx.textBaseline = high ? "bottom" : "top";
            ctx.strokeStyle = c.surface;
            ctx.lineWidth = 3;
            ctx.strokeText(word, x, ty);
            ctx.fillStyle = tone;
            ctx.fillText(word, x, ty);
            const w = ctx.measureText(word).width + 4;
            placed.push({ x: x - w / 2, y: high ? ty - 12 : ty, w, h: 12 });
          });
        };
        mark(sw.highs, true);
        mark(sw.lows, false);
      }

      if (this.show.sr) {
        ctx.font = `600 10px ${c.font}`;
        for (const l of this.data.levels) {
          const y = s.priceToCoordinate(l.price);
          const xLast = xOf(l.touches.at(-1)!.time);
          if (y === null || xLast === null) continue;
          // The dots and the line's weight show the touches; the tag names the level.
          const text = `${l.kind === "resistance" ? "Resistance" : "Support"} ${this.format(l.price)}`;
          const w = ctx.measureText(text).width + 2 * TAG_PAD;
          // Just right of the latest turn (price moved away from there), on the
          // far side of the line from the price; else at the right edge.
          const maxX = mediaSize.width - this.insets.rightLane - w - 4;
          const ty = l.kind === "resistance" ? y - TAG_H - 3 : y + 3;
          const at = (x: number) => ({ x: Math.max(2, Math.min(x, maxX)), y: ty, w, h: TAG_H });
          const r = [at(xLast + 10), at(maxX), at(xLast - w - 10)].find(r => !clash(r));
          if (!r || r.y < 0 || r.y + TAG_H > mediaSize.height) continue;
          placed.push(r);
          this.tag(ctx, r, text, c.ink);
        }
      }
      ctx.restore();
    });
  }

  /** The signal overlay's tag look: surface pill, tinted, outlined. */
  private tag(ctx: Ctx, r: Rect, text: string, color: string) {
    ctx.beginPath();
    ctx.roundRect(r.x, r.y, r.w, TAG_H, 3);
    ctx.fillStyle = this.colors.surface;
    ctx.fill();
    ctx.fillStyle = withAlpha(color, 0.12);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = withAlpha(color, 0.6);
    ctx.stroke();
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillStyle = color;
    ctx.fillText(text, r.x + TAG_PAD, r.y + TAG_H / 2 + 0.5);
  }
}
